import {
  ComponentProps,
  ElementType,
  createElement,
  useEffect,
  useMemo,
  useState,
} from "react"
import { TAreaChartProps } from "../AreaChart"
import { TComposedChartProps } from "../ComposedChart/ComposedChart"
import {
  AggregationType,
  TOTAL_CALCULATED_DATA_KEY,
  VISUALIZATION_TYPE,
  VISUALIZATIONS_MAP,
} from "~/ui-rtk/constants/charts"
import useMetricsCache from "~/ui-rtk/hooks/metrics-cache"
import ChartDataLoader from "../ChartDataLoader"
import {
  GranularityMapping,
  MetricDataItem,
  MetricsMapping,
  TMetricItem,
} from "~/ui-rtk/constants/metrics-mapping"
import { TableColumn, type TimeDimensionGranularity } from "@cubejs-client/core"
import {
  getColorForMetric,
  getColorFromPaletteByIdx,
} from "~/ui-rtk/utils/chart-utils"
import useHighlightMetric from "~/ui-rtk/hooks/highlight-metric"
import { isNumeric } from "~/ui-rtk/utils/format"
import { RowSelectionState, SortingState } from "@tanstack/react-table"
import { ITEMS_PER_PAGE } from "../TableVisualization/constants"
import { LocalStorageKeys, useLocalStorage } from "~/ui-rtk/utils/storage"
import { composeMetrics } from "../ComposedTable/utils"
import { TCubeFilterOptions } from "~/ui-rtk/shared/types/charts"
import { TDrawerConfig } from "~/ui-rtk/constants/drawer"
import useUrlColumnFilters from "~/ui-rtk/hooks/url-column-filters"
import useUrlPagination from "~/ui-rtk/hooks/url-pagination"
import useUrlSorting from "~/ui-rtk/hooks/url-sorting"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import useCubeFiltersFromColumnFilters from "~/ui-rtk/hooks/cube-filters-from-column-filters"
import useWeekGranularity from "~/ui-rtk/hooks/week-granularity"
import { useChartCSVExport } from "~/ui-rtk/hooks/use-chart-csv-export"
import { Tag } from "~/ui-rtk/types/tags"
import {
  TableVisualizationColumn,
  TSources,
} from "~/ui-rtk/constants/table-visualization-types"
import { ConfigType } from "~/ui-rtk/components/ui/charts/ComposedTable/types"
import { type AssetFieldsConfig } from "~/ui-rtk/app/slices/asset-cache.slice"

export type TChartProps = {
  component: ElementType
  chartId: string
  urlParamsPrefix?: string
  dateRange?: [Date, Date]
  widgetType: VISUALIZATION_TYPE
  setDataAggType?: (type: AggregationType) => void
  granularity?: AggregationType
  compareRange?: [Date, Date]
  className?: string
  highlightMetric?: string
  highlightMetricPrefix?: string
  highlightMetricSuffix?: string
  manualTotal?: boolean
  enablePagination?: boolean
  cubeFilters?: TCubeFilterOptions
  sortBy?: string
  sortDesc?: boolean
  limit?: number
  disableGranularity?: boolean
  defaultSorting?: SortingState
  cubeQueryParams?: Record<string, unknown>
  comparisonEnabled?: boolean
  compareUniqueKey?: string
  description?: string
  manualSort?: boolean
  prefixEnabled?: boolean
  queryPrefix?: string | null
  useOrderQueryPrefix?: boolean
  itemComponent?: ElementType
  openDrawer?: (config: Partial<TDrawerConfig>) => void
  /**
   * Override row-click behavior on TableVisualization-backed charts. When
   * set, fires instead of the default drawer-opening path. Used by
   * AdBlaster's tabs to toggle row selection on click.
   */
  onRowClick?: (row: TMetricItem) => void
  /** Table only — see TableVisualization. Forwarded with the rest of the props. */
  onRowsRendered?: (rows: TMetricItem[]) => void
  useLocalStoragePagination?: boolean
  defaultItemsPerPage?: number
  enableRowSelection?: boolean
  rowSelection?: RowSelectionState
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void
  enableCSVExport?: boolean
  csvExportFilename?: string
  enableChartDataExport?: boolean
  chartDataExportFilename?: string
  // Tagging system props
  tags?: Tag[]
  onTagsChange?: (itemId: string, tags: Tag[]) => void
  // Additional props for specific chart types
  columnConfig?: TableVisualizationColumn[]
  highlightCells?: boolean
  groupValue?: string
  customSources?:
    | TSources
    | Array<{ dataKey: string; name: string; requiredSource: string }>
  data?: Array<Record<string, string | number | boolean>>
  summaryWidget?: string
  defaultColumnKeys?: string[]
  loading?: boolean
  error?: unknown
  isLoading?: boolean
  showPostsLink?: boolean
  showAdsLink?: boolean
  resolveAssets?: boolean
  assetFields?: AssetFieldsConfig
  selectedSources?: string[]
  setSelectedSources?: (sources: string[]) => void
  onSettingClick?: (() => void) | null
  // Dynamic bar chart mode props
  valueKey?: string
  labelKey?: string
  maxBars?: number
} & (Omit<TAreaChartProps, "metrics"> | Omit<TComposedChartProps, "metrics">)

export default function Chart($props: TChartProps) {
  const {
    component,
    dateRange,
    compareRange,
    chartId,
    urlParamsPrefix,
    widgetType,
    granularity,
    dataAggTypeOnly,
    highlightMetric,
    highlightMetricPrefix,
    highlightMetricSuffix,
    enablePagination = false,
    cubeFilters: cubeFiltersFromProps,
    sortBy,
    sortDesc = true,
    limit,
    disableGranularity = false,
    cubeQueryParams,
    comparisonEnabled = false,
    compareUniqueKey = "id",
    setDataAggType = () => null,
    manualSort = false,
    useLocalStoragePagination = true,
    defaultItemsPerPage = 10,
    prefixEnabled = false,
    defaultSorting,
    useOrderQueryPrefix = false,
    queryPrefix = null,
    enableRowSelection = false,
    rowSelection,
    onRowSelectionChange,
    enableCSVExport = false,
    csvExportFilename,
    enableChartDataExport = false,
    chartDataExportFilename,
    tags = [],
    onTagsChange,
    resolveAssets = false,
    assetFields,
    valueKey,
    labelKey,
    maxBars = 5,
    ...props
  } = $props
  const [, onCacheSet] = useState<unknown>()

  const [itemsPerPage, setItemsPerPage] = useLocalStorage<number>(
    LocalStorageKeys.TABLE_VISUALIZATION_ITEMS_PER_PAGE,
    ITEMS_PER_PAGE,
  )
  const defaultPageSize = useLocalStoragePagination
    ? itemsPerPage
    : defaultItemsPerPage
  const { pagination: paginationState, setPagination: setPaginationState } =
    useUrlPagination(chartId, defaultPageSize)

  const { sorting } = useUrlSorting(
    urlParamsPrefix ?? chartId,
    defaultSorting,
    useOrderQueryPrefix ? queryPrefix : null,
  )

  const propsSorting = sortBy
    ? [
        {
          id: sortBy,
          desc: sortDesc,
        },
      ]
    : []

  const paginationStatePerPage = paginationState.pageSize

  const { columnFilters } = useUrlColumnFilters(urlParamsPrefix ?? chartId)

  const cubeFilters = useMemo(() => {
    const extraFilters = useCubeFiltersFromColumnFilters(
      queryPrefix,
      columnFilters,
    )

    if (cubeFiltersFromProps) {
      return {
        ...cubeFiltersFromProps,
        extraFilters,
      }
    }

    return { extraFilters }
  }, [columnFilters, cubeFiltersFromProps])

  // Extract extraSorting from VISUALIZATIONS_MAP column configuration
  // Only include extraSorting for columns that are currently being sorted
  const extraSorting = useMemo(() => {
    const visualization = VISUALIZATIONS_MAP[chartId]
    if (!visualization?.props?.columnConfig) return []

    const currentSorting = manualSort ? propsSorting : sorting
    if (!currentSorting || currentSorting.length === 0) return []

    const sortedColumnIds = new Set(currentSorting.map(sort => sort.id))

    return visualization.props.columnConfig
      .filter(
        (col: TableVisualizationColumn) =>
          col.extraSorting && sortedColumnIds.has(col.key),
      )
      .map((col: TableVisualizationColumn) => col.extraSorting)
  }, [chartId, manualSort, propsSorting, sorting])

  // Provide default empty date range if not provided (for cases where data is passed directly)
  const safeDateRange = dateRange ?? ([new Date(), new Date()] as [Date, Date])

  const { isInProgress, data, setCacheItem } = useMetricsCache(
    chartId,
    dataAggTypeOnly ?? granularity ?? AggregationType.DAILY,
    safeDateRange,
    onCacheSet,
    paginationState.pageIndex,
    limit ?? paginationState.pageSize,
    cubeFilters,
    manualSort ? propsSorting : sorting,
    cubeQueryParams?.[chartId] as Record<string, string> | undefined,
  )

  const {
    isInProgress: compareIsInProgress,
    data: compareData,
    setCacheItem: compareSetCacheItem,
  } = useMetricsCache(
    chartId,
    dataAggTypeOnly ?? granularity ?? AggregationType.DAILY,
    compareRange as unknown as [Date, Date], // TypeCheck fallback.
    onCacheSet,
    paginationState.pageIndex,
    limit ?? paginationState.pageSize,
    cubeFilters,
    manualSort ? propsSorting : sorting,
    cubeQueryParams?.[chartId] as Record<string, string> | undefined,
  )
  const currentCompany = useCurrentCompany()

  const highlightKey = useHighlightMetric(
    highlightMetric,
    highlightMetricPrefix,
    highlightMetricSuffix,
  )

  // CSV Export functionality
  const visualization = VISUALIZATIONS_MAP[chartId]
  const columnConfig = visualization?.props?.columnConfig || []

  const csvExport = useChartCSVExport({
    chartId,
    dateRange: safeDateRange,
    compareRange: comparisonEnabled ? compareRange : undefined,
    granularity: dataAggTypeOnly ?? granularity ?? AggregationType.DAILY,
    cubeFilters,
    columnFilters,
    queryPrefix,
    columnConfig,
    sorting: manualSort ? propsSorting : sorting,
    defaultFilename: csvExportFilename || chartId,
  })

  // Resolve WEEKLY to the company's reporting week granularity (e.g.
  // `week_thursday`) so filters that build `<dimension>.<granularity>` keys
  // match the keys actually present in the cube response.
  const weekGranularity = useWeekGranularity()
  const aggregationType =
    dataAggTypeOnly ?? granularity ?? AggregationType.DAILY
  const granularityValue =
    aggregationType === AggregationType.WEEKLY
      ? (weekGranularity as TimeDimensionGranularity)
      : GranularityMapping[aggregationType]

  if (!MetricsMapping?.[chartId]) {
    console.error(MetricsMapping, chartId)
  }
  const { filter } = MetricsMapping[chartId]

  const inProgressContent = []

  const cacheSet = !isInProgress()

  if (!cacheSet) {
    if (filter) {
      filter([], granularityValue, {
        currentCompany,
      })
    }
    inProgressContent.push(
      <ChartDataLoader
        {...props}
        key="main-chart"
        className={props.className}
        chartId={chartId}
        dateRange={safeDateRange}
        widgetType={widgetType}
        granularity={dataAggTypeOnly ?? granularity ?? AggregationType.DAILY}
        page={paginationState.pageIndex}
        itemsPerPage={limit ?? paginationState.pageSize}
        setCacheItem={
          setCacheItem as ComponentProps<typeof ChartDataLoader>["setCacheItem"]
        }
        cubeFilters={cubeFilters}
        prefixEnabled={prefixEnabled}
        sorting={manualSort ? propsSorting : sorting}
        extraSorting={extraSorting}
        enablePagination={enablePagination}
        disableGranularity={disableGranularity}
        cubeQueryParams={
          cubeQueryParams?.[chartId] as Record<string, string> | undefined
        }
        component={component}
      />,
    )
  }

  const compareCacheSet = !compareIsInProgress()

  if (comparisonEnabled && !compareCacheSet && compareRange) {
    if (filter) {
      filter([], granularityValue, {
        currentCompany,
      })
    }
    inProgressContent.push(
      <ChartDataLoader
        {...props}
        key="compare-chart"
        className={props.className}
        chartId={chartId}
        widgetType={widgetType}
        dateRange={compareRange}
        granularity={dataAggTypeOnly ?? granularity ?? AggregationType.DAILY}
        page={limit ?? paginationState.pageIndex}
        itemsPerPage={paginationState.pageSize}
        setCacheItem={
          compareSetCacheItem as ComponentProps<
            typeof ChartDataLoader
          >["setCacheItem"]
        }
        cubeFilters={cubeFilters}
        sorting={manualSort ? propsSorting : sorting}
        extraSorting={extraSorting}
        prefixEnabled={prefixEnabled}
        enablePagination={enablePagination}
        disableGranularity={disableGranularity}
        cubeQueryParams={
          cubeQueryParams?.[chartId] as Record<string, string> | undefined
        }
        component={component}
      />,
    )
  }

  useEffect(() => {
    if (paginationStatePerPage !== itemsPerPage && useLocalStoragePagination) {
      setItemsPerPage(paginationStatePerPage)
    }
  }, [
    paginationStatePerPage,
    itemsPerPage,
    setItemsPerPage,
    useLocalStoragePagination,
  ])

  // Asset resolution moved into the cube data step (postProcess) so it fires
  // alongside tag fetching and only once per page. See connect.ts and
  // resolveRowAssets in metrics-mapping.ts. The `resolveAssets` and
  // `assetFields` props are kept on TChartProps for callsite back-compat but
  // no longer drive a separate effect here.

  if (inProgressContent.length > 0) {
    return inProgressContent[0]
  }

  const { columns, dataSource, totalPages } = data as {
    columns: TableColumn[]
    dataSource: Record<string, unknown>[]
    totalPages?: number | null
  }
  const { dataSource: compareDataSource } = (compareData ?? {}) as {
    dataSource: Record<string, unknown>[]
  }

  const [dateColumn, ...dataColumns] = columns
  let isCompareMetricMain = false

  let metrics
  let color
  let barChartConfig:
    | { label: string; dataKey: string; color: string }[]
    | undefined
  switch (widgetType) {
    case VISUALIZATION_TYPE.AreaChart: {
      if (comparisonEnabled && compareRange) {
        isCompareMetricMain = dataSource.length <= compareDataSource.length
        const dataSources = isCompareMetricMain
          ? [compareDataSource, dataSource]
          : [dataSource, compareDataSource]
        const [mainDataSource, secondaryDataSource] = dataSources

        metrics = ((mainDataSource ?? []) as TMetricItem[]).map(
          (metrics: TMetricItem, idx: number) => ({
            value: metrics[dataColumns[0]?.key],
            comparedValue: secondaryDataSource[idx]
              ? secondaryDataSource[idx][dataColumns[0]?.key]
              : 0,
            date: metrics.date,
            compareDate: secondaryDataSource[idx]?.date ?? null,
            metadata: dataColumns[0],
          }),
        )
      } else {
        metrics = ((dataSource ?? []) as TMetricItem[]).map(
          (metrics: TMetricItem) => ({
            value: metrics[dataColumns[0]?.key],
            date: metrics.date,
            metadata: dataColumns[0],
          }),
        )
      }
      color = getColorForMetric(dataColumns[0]?.key)
      break
    }
    case VISUALIZATION_TYPE.ComposedChart:
    case VISUALIZATION_TYPE.MultiChart: {
      if (comparisonEnabled && compareRange) {
        isCompareMetricMain = dataSource.length <= compareDataSource.length

        const dataSources = isCompareMetricMain
          ? [compareDataSource, dataSource]
          : [dataSource, compareDataSource]
        const [mainDataSource, secondaryDataSource] = dataSources

        metrics = ((mainDataSource ?? []) as TMetricItem[]).map(
          (metrics: TMetricItem, idx: number) => {
            const comparedDate = secondaryDataSource[idx]
              ? (secondaryDataSource[idx][dateColumn.key] ??
                secondaryDataSource[idx].date)
              : null

            const metric: Record<string, number | string | boolean> = {
              date: metrics[dateColumn.key],
              [`compared.date`]: comparedDate as string | number,
              [`compared.${dateColumn.key}`]: comparedDate as string | number,
              ...metrics,
            }
            if (
              secondaryDataSource[idx] &&
              secondaryDataSource[idx][TOTAL_CALCULATED_DATA_KEY]
            ) {
              metric[`compared.${TOTAL_CALCULATED_DATA_KEY}`] =
                secondaryDataSource[idx][TOTAL_CALCULATED_DATA_KEY] as
                  | string
                  | number
            }
            dataColumns.forEach((dataColumn: TableColumn) => {
              const nullishValue = dataColumn.type === "number" ? 0 : ""
              metric[dataColumn.dataIndex] =
                metrics[dataColumn.dataIndex] ?? nullishValue
              metric[`compared.${dataColumn.dataIndex}`] = secondaryDataSource[
                idx
              ]
                ? (secondaryDataSource[idx][dataColumn.dataIndex] as
                    | string
                    | number)
                : nullishValue
            })

            if ($props.manualTotal && !metric[TOTAL_CALCULATED_DATA_KEY]) {
              const total = dataColumns.reduce(
                (acc: number, { key }: { key: string }) => {
                  if (isNumeric(mainDataSource[idx][key])) {
                    return acc + parseFloat(`${mainDataSource[idx][key]}`)
                  }
                  return acc
                },
                0,
              )
              const compareTotal = dataColumns.reduce(
                (acc: number, { key }: { key: string }) => {
                  if (isNumeric(secondaryDataSource[idx]?.[key])) {
                    return acc + parseFloat(`${secondaryDataSource[idx][key]}`)
                  }
                  return acc
                },
                0,
              )
              metric[TOTAL_CALCULATED_DATA_KEY] = total
              metric[`compared.${TOTAL_CALCULATED_DATA_KEY}`] = compareTotal
            }
            return metric
          },
        )
      } else {
        metrics = ((dataSource ?? []) as TMetricItem[]).map(
          (metrics: TMetricItem) => {
            const metric: Record<string, number | string | boolean> = {
              date: metrics[dateColumn.key],
              ...metrics,
            }
            if ($props.manualTotal && !metric[TOTAL_CALCULATED_DATA_KEY]) {
              const total = dataColumns.reduce(
                (acc: number, { key }: { key: string }) => {
                  if (isNumeric(metric[key])) {
                    return acc + parseFloat(`${metric[key]}`)
                  }
                  return acc
                },
                0,
              )
              metric[TOTAL_CALCULATED_DATA_KEY] = total
            }
            return metric
          },
        )
      }
      break
    }
    case VISUALIZATION_TYPE.CohortVisualization:
    case VISUALIZATION_TYPE.TableVisualization:
    case VISUALIZATION_TYPE.SimpleTable:
    case VISUALIZATION_TYPE.SortedItems: {
      if (comparisonEnabled && compareRange) {
        metrics = [dataSource, compareDataSource]
      } else {
        metrics = [...dataSource]
      }
      break
    }
    case VISUALIZATION_TYPE.ComposedTable:
      // eslint-disable-next-line no-case-declarations
      const widget = VISUALIZATIONS_MAP[chartId]
      // eslint-disable-next-line no-case-declarations
      const defaultWidgetConfig = {
        columnConfig: [],
        calculateTotal: false,
      }
      if (comparisonEnabled && compareRange) {
        const [mainDataSource, secondaryDataSource] = [
          dataSource,
          compareDataSource,
        ]
        metrics = ((mainDataSource ?? []) as TMetricItem[]).map(
          (metrics: TMetricItem, idx: number) => {
            const metric: Record<string, number | string | boolean> = {
              ...metrics,
            }
            Object.keys(metrics).forEach((key: string) => {
              metric[key] = metrics[key] ?? 0
              metric[`compared.${key}`] = secondaryDataSource[idx]
                ? +(secondaryDataSource[idx][key] as string | number)
                : 0
            })
            return metric
          },
        )
        metrics = composeMetrics(
          metrics || [],
          (widget.props ?? defaultWidgetConfig) as {
            columnConfig: ConfigType
            calculateTotal?: boolean
          },
          true,
        )
      } else {
        metrics = composeMetrics(
          dataSource || [],
          (widget.props ?? defaultWidgetConfig) as {
            columnConfig: ConfigType
            calculateTotal?: boolean
          },
        )
      }
      break
    case VISUALIZATION_TYPE.BarChart: {
      isCompareMetricMain = false

      // Dynamic-data mode: rows represent individual bars (e.g. one per theme)
      if (valueKey && labelKey) {
        // Build synthetic config + flat metrics from multi-row data
        const dynamicConfig: {
          label: string
          dataKey: string
          color: string
        }[] = []
        const flatMetrics: Record<string, number> = {}

        const rows = dataSource ?? []
        const topRows = rows.slice(0, maxBars)
        const remainingRows = rows.slice(maxBars)

        topRows.forEach((row: Record<string, unknown>, idx: number) => {
          const barKey = `dynamic_bar_${idx}`
          dynamicConfig.push({
            label: String(row[labelKey] ?? `Item ${idx}`),
            dataKey: barKey,
            color: getColorFromPaletteByIdx(idx),
          })
          flatMetrics[barKey] = Number(row[valueKey] ?? 0)

          // Handle comparison data
          if (compareDataSource?.length) {
            const compareRow = compareDataSource.find(
              (cr: Record<string, unknown>) => cr[labelKey] === row[labelKey],
            )
            flatMetrics[`compared.${barKey}`] = compareRow
              ? Number(compareRow[valueKey] ?? 0)
              : 0
          }
        })

        // Aggregate remaining rows into an "Other" bar (summed)
        if (remainingRows.length > 0) {
          const otherKey = `dynamic_bar_other`
          let otherSum = 0
          let otherCompareSum = 0

          remainingRows.forEach((row: Record<string, unknown>) => {
            otherSum += Number(row[valueKey] ?? 0)

            if (compareDataSource?.length) {
              const compareRow = compareDataSource.find(
                (cr: Record<string, unknown>) => cr[labelKey] === row[labelKey],
              )
              otherCompareSum += compareRow
                ? Number(compareRow[valueKey] ?? 0)
                : 0
            }
          })

          dynamicConfig.push({
            label: "Other",
            dataKey: otherKey,
            color: getColorFromPaletteByIdx(maxBars),
          })
          flatMetrics[otherKey] = otherSum

          if (compareDataSource?.length) {
            flatMetrics[`compared.${otherKey}`] = otherCompareSum
          }
        }

        metrics = flatMetrics
        barChartConfig = dynamicConfig
      } else {
        // Static-config mode (e.g. brand_value_by_channel_group_chart)
        const mainDataSource = dataSource?.[0] ?? {}
        const secondaryDataSource = compareDataSource?.[0] ?? {}

        const newData: Record<string, unknown> = {}
        Object.keys(mainDataSource).forEach((key: string) => {
          newData[key] = mainDataSource[key]
          newData[`compared.${key}`] = secondaryDataSource[key]
        })

        metrics = newData
      }
      break
    }
    default: {
      return (
        <div className="flex items-center justify-center h-full">
          {widgetType} To Be Fixed
        </div>
      )
    }
  }

  if (filter) {
    metrics = filter(metrics as MetricDataItem[], granularityValue, {
      currentCompany,
      compareMode: comparisonEnabled && compareRange,
    })
  }

  const chartProps = {
    ...props,
    ...(barChartConfig && { config: barChartConfig, dataKey: undefined }),
    metrics,
    color,
    chartId,
    urlParamsPrefix,
    dataAggType: granularity,
    dataAggTypeOnly,
    compareMode: Boolean(comparisonEnabled && compareRange && compareData),
    isCompareMetricMain,
    highlightKey,
    setDataAggType,
    setPaginationState,
    paginationState,
    columnFilters,
    totalPages,
    sortBy,
    sortDesc,
    dateRange,
    enablePagination,
    compareUniqueKey,
    cubeFilters,
    compareRange,
    cubeQueryParams: cubeQueryParams?.[chartId],
    enableRowSelection,
    rowSelection,
    onRowSelectionChange,
    // CSV Export props
    ...(enableCSVExport && {
      csvExport: csvExport.exportCSV,
      isExporting: csvExport.isExporting,
      exportProgress: csvExport.progress,
    }),
    // Chart Data Export props
    ...(enableChartDataExport && {
      enableChartDataExport: true,
      chartDataExportFilename,
    }),
    // Tagging props
    tags,
    onTagsChange,
  }

  return createElement(component, chartProps)
}
